import { relations, sql } from 'drizzle-orm'
import {
  type AnyPgColumn,
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core'
import {
  coachingHomeworkExercise,
  coachingHomeworkSourceContent,
  coachingHomeworkSystemDesign,
  coachingHomeworkTask,
} from './coaching.js'
import {
  systemDesignAssertionGradedFromEnum,
  systemDesignCompanyContextEnum,
  systemDesignInterviewTypeEnum,
} from './enums.js'
import { systemDesignInterviewExperience } from './interview-experiences.js'
import { companyTag, difficultyLevel, source } from './problems.js'
import { studyPlanResource, studyPlanSystemDesign } from './study-plans.js'

// =============================================================================
// EXPANDED CONTENT TYPES FOR COACHING
// =============================================================================

/**
 * CodingExercise - Focused skill-building exercises (shorter than full problems)
 * Examples: "Implement binary search", "Reverse a linked list", "Sliding window practice"
 */
export const codingExercise = pgTable('CodingExercise', {
  id: uuid('id').primaryKey().defaultRandom(),
  title: text('title').notNull(),
  slug: text('slug').notNull().unique(),
  description: text('description'),
  difficultyId: uuid('difficultyId').references(() => difficultyLevel.id, {
    onDelete: 'set null',
  }),
  starterCode: jsonb('starterCode').$type<Record<string, string>>(), // { python: "def foo():", javascript: "function foo() {}" }
  solution: jsonb('solution').$type<Record<string, string>>(), // { python: "...", javascript: "..." }
  hints: text('hints').array().default([]).notNull(),
  estimatedMinutes: integer('estimatedMinutes'),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

/**
 * One phase of a system-design interview and how long it should take.
 *
 * The canonical schedule lives at the interview-*type* level and is not built
 * yet, so this is only ever the per-problem override (LG-922 decision 8):
 * grader-only metadata, never shown to the learner mid-session.
 */
export interface SystemDesignPhase {
  /** Phase name, e.g. "requirements", "high-level design", "deep dive". */
  phase: string
  /** Target minutes for the phase, out of the problem's `timeCap`. */
  targetMinutes: number
}

/**
 * SystemDesignProblem - Design interview questions without code execution
 * Examples: "Design Twitter", "Design a URL Shortener", "Design Netflix"
 */
export const systemDesignProblem = pgTable(
  'SystemDesignProblem',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    title: text('title').notNull(),
    slug: text('slug').notNull().unique(),
    description: text('description'),
    difficultyId: uuid('difficultyId').references(() => difficultyLevel.id, {
      onDelete: 'set null',
    }),
    /**
     * The source this problem's content belongs to (BCTCI, Hello Interview, …),
     * mirroring `Problem.sourceId`. `SystemDesignProblemSource` rows are pure
     * "also listed on" attribution and cannot answer this — see the junction.
     */
    sourceId: uuid('sourceId').references(() => source.id, {
      onDelete: 'set null',
    }),
    topics: text('topics').array().default([]).notNull(), // ["distributed systems", "databases", "caching"]
    hints: text('hints').array().default([]).notNull(),
    sampleSolution: text('sampleSolution'),
    /** Editorial for the whiteboard page's editorial pane. Markdown for v1. */
    editorialMarkdown: text('editorialMarkdown'),
    /**
     * Interview flavour and scale, both authored per problem and both feeding
     * prompting *and* grading (LG-922 decision 2). Nullable: the existing
     * catalog predates them and an unset problem falls back to the interviewer's
     * generic behaviour rather than being mislabelled.
     */
    interviewType: systemDesignInterviewTypeEnum('interviewType'),
    companyContext: systemDesignCompanyContextEnum('companyContext'),
    /**
     * Wall-clock budget for one session, in minutes. Distinct from
     * `estimatedMinutes`, which is the catalog's "how long is this" estimate
     * used by coaching assignments — this one bounds the interview itself and
     * is what the five-minute warning counts back from.
     */
    timeCap: integer('timeCap'),
    /** Per-problem override of the interview type's phase schedule. */
    phaseSchedule: jsonb('phaseSchedule').$type<SystemDesignPhase[]>(),
    estimatedMinutes: integer('estimatedMinutes'),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [index('SystemDesignProblem_sourceId_idx').on(table.sourceId)],
)

/**
 * Task - Reusable task templates (not one-off assignments)
 * Examples: "Review Big O notation", "Complete Binary Search chapter", "Set up coding environment"
 */
export const task = pgTable(
  'Task',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    title: text('title').notNull(),
    notes: text('notes'), // Mike's Notes - internal notes about the task
    url: text('url'),
    estimatedMinutes: integer('estimatedMinutes'),
    category: text('category'), // "reading", "practice", "setup", etc.
    sourceId: uuid('sourceId').references(() => source.id, {
      onDelete: 'set null',
    }),
    sourceContentId: uuid('sourceContentId').references(
      () => sourceContent.id,
      { onDelete: 'set null' },
    ),
    resourceId: uuid('resourceId').references(() => resource.id, {
      onDelete: 'set null',
    }),
    contentBody: text('contentBody'), // Markdown content for Notion page body
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [
    index('Task_sourceId_idx').on(table.sourceId),
    index('Task_sourceContentId_idx').on(table.sourceContentId),
    index('Task_resourceId_idx').on(table.resourceId),
  ],
)

/**
 * SourceContent - Structured content within a Source (chapters, playlists, modules, etc.)
 * Examples: "Chapter 5: Binary Search" (book), "NeetCode 150 Playlist" (YouTube), "Arrays Module" (course)
 * This replaces the need for separate "ResourceCollection" and "book chapter" concepts
 */
export const sourceContent = pgTable(
  'SourceContent',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    sourceId: uuid('sourceId')
      .notNull()
      .references(() => source.id, { onDelete: 'cascade' }),
    title: text('title').notNull(), // "Chapter 5: Binary Search", "NeetCode 150"
    slug: text('slug').notNull(),
    description: text('description'),
    kind: text('kind').notNull(), // free-text container kind ("chapter", "quiz", ...)
    actionVerb: text('actionVerb'), // optional verb for assigning the container ("Read", "Watch")
    order: integer('order').default(0).notNull(), // Position within source (chapter number)
    url: text('url'), // Link to the content
    parentId: uuid('parentId').references((): AnyPgColumn => sourceContent.id, {
      onDelete: 'restrict',
    }), // Self-referential: for nested content (section within chapter)
    estimatedMinutes: integer('estimatedMinutes'),
    icon: text('icon'), // Optional icon override (emoji, URL, or filename in static/icons/)
    /**
     * Whether a coach may cascade-assign this container as a unit (LG-907).
     *
     * Default true, so nothing in today's catalog behaves differently until a
     * flag is flipped. False marks a container that only groups — a whole book,
     * a "misc" bucket — where "assign this" has no coherent meaning and the
     * coach should reach for a chapter instead. It gates the Assign affordance
     * only; membership, nesting and rollups are unaffected.
     */
    isAssignableUnit: boolean('isAssignableUnit').default(true).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [
    unique('SourceContent_sourceId_slug_unique').on(table.sourceId, table.slug),
    index('SourceContent_sourceId_idx').on(table.sourceId),
    index('SourceContent_parentId_idx').on(table.parentId),
    index('SourceContent_parentId_order_idx').on(
      table.parentId,
      table.order,
      table.id,
    ),
    // kind is load-bearing free text (public reads compare eq(kind, 'chapter'));
    // writes normalize via normalizeKind — this backstops the non-empty invariant
    check('SourceContent_kind_nonempty_check', sql`${table.kind} <> ''`),
  ],
)

/**
 * CodingExerciseSource - Junction table linking exercises to sources
 * Same pattern as ProblemSource - exercises can appear on multiple platforms
 */
export const codingExerciseSource = pgTable(
  'CodingExerciseSource',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    exerciseId: uuid('exerciseId')
      .notNull()
      .references(() => codingExercise.id, { onDelete: 'cascade' }),
    sourceId: uuid('sourceId')
      .notNull()
      .references(() => source.id, { onDelete: 'cascade' }),
    externalId: text('externalId'), // ID in source's system
    url: text('url'), // Link on source platform
    order: integer('order').default(0).notNull(), // 0 = primary source
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    // NO deletedAt - junction table
  },
  (table) => [
    unique('CodingExerciseSource_exerciseId_sourceId_unique').on(
      table.exerciseId,
      table.sourceId,
    ),
    index('CodingExerciseSource_exerciseId_idx').on(table.exerciseId),
    index('CodingExerciseSource_sourceId_idx').on(table.sourceId),
    index('CodingExerciseSource_order_idx').on(table.order),
  ],
)

/**
 * SystemDesignProblemSource - Junction table linking system design problems to sources
 *
 * SystemDesignProblem owns its identity. These rows are secondary "also listed
 * on" attribution metadata only — the same contract `ProblemSource` carries,
 * and the shape is deliberately field-for-field identical to it (LG-930). The
 * junction predated coding's identity rework and had inherited the old shape:
 * a per-source `slug`, `difficultyId` and `order` that all restated something
 * the problem itself owns.
 */
export const systemDesignProblemSource = pgTable(
  'SystemDesignProblemSource',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => systemDesignProblem.id, { onDelete: 'cascade' }),
    sourceId: uuid('sourceId')
      .notNull()
      .references(() => source.id, { onDelete: 'cascade' }),
    title: text('title').notNull(),
    externalId: text('externalId'),
    premiumRequired: boolean('premiumRequired').default(false).notNull(),
    url: text('url'),
    editorialUrl: text('editorialUrl'),
    /**
     * Marks the source the problem *originated* on, which is not the same
     * question as `SystemDesignProblem.sourceId` (whose content this is).
     * Attribution-only, so it lives here rather than on the problem, which
     * owns identity. At most one row per problem may set it; see the partial
     * unique index below.
     */
    isOriginal: boolean('isOriginal').default(false).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    // NO deletedAt - junction table
  },
  (table) => [
    unique('SystemDesignProblemSource_problemId_sourceId_unique').on(
      table.problemId,
      table.sourceId,
    ),
    index('SystemDesignProblemSource_problemId_idx').on(table.problemId),
    index('SystemDesignProblemSource_sourceId_idx').on(table.sourceId),
    // Partial, exactly as on ProblemSource: a problem may hold many unmarked
    // sources but at most one original. "Exactly one" is deliberately not
    // attempted — most problems mark none and resolve to the owning source.
    uniqueIndex('SystemDesignProblemSource_problemId_isOriginal_unique')
      .on(table.problemId)
      .where(sql`${table.isOriginal}`),
  ],
)

/**
 * SystemDesignProblemCompanyTag - Which company tags a problem is asked under
 *
 * Replaces the free-text `SystemDesignProblem.company` ("Netflix, Amazon,
 * Google"), which could not be filtered, deduplicated, or renamed (LG-930).
 * Tags come from the admin-curated `CompanyTag` vocabulary the companies admin
 * already maintains.
 */
export const systemDesignProblemCompanyTag = pgTable(
  'SystemDesignProblemCompanyTag',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => systemDesignProblem.id, { onDelete: 'cascade' }),
    companyTagId: uuid('companyTagId')
      .notNull()
      .references(() => companyTag.id, { onDelete: 'cascade' }),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    // NO deletedAt - junction table
  },
  (table) => [
    unique('SystemDesignProblemCompanyTag_problemId_companyTagId_unique').on(
      table.problemId,
      table.companyTagId,
    ),
    index('SystemDesignProblemCompanyTag_problemId_idx').on(table.problemId),
    index('SystemDesignProblemCompanyTag_companyTagId_idx').on(
      table.companyTagId,
    ),
  ],
)

/**
 * SystemDesignAssertion - the "tests on your design" bank, authored per problem
 *
 * One row is one thing the grader looks for in a session: `whatWeCheck` names
 * it for the learner, `expected` says exactly what counts, and `insufficient`
 * spells out the answers that look right and are not. `insufficient` is its own
 * column rather than a paragraph inside `expected` (LG-931) because it is the
 * half a grader most often ignores when it is buried in prose.
 *
 * Authored content, so soft-deletable: an assertion retired today is still
 * referenced by every `CheckResult` already graded against it, and hard
 * deleting it would rewrite finished sessions.
 */
export const systemDesignAssertion = pgTable(
  'SystemDesignAssertion',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => systemDesignProblem.id, { onDelete: 'cascade' }),
    /** The learner-facing title of the check ("Cache sits in front of reads"). */
    whatWeCheck: text('whatWeCheck').notNull(),
    /** Exactly what the AI looks for, across the transcript and the board. */
    expected: text('expected').notNull(),
    /**
     * The explicitly not-good-enough answers. Nullable: plenty of checks are
     * pass/fail enough that there is no near-miss worth naming, and an empty
     * string would read as "the author wrote one" to anyone reviewing the bank.
     */
    insufficient: text('insufficient'),
    /**
     * Which evidence the grader may credit. Defaults to the wider window: a
     * check authored without thinking about it should not silently ignore what
     * the candidate said.
     */
    gradedFrom: systemDesignAssertionGradedFromEnum('gradedFrom')
      .default('board_and_conversation')
      .notNull(),
    /**
     * The freebie check, visible mid-session — typically "an architecture
     * diagram was drawn". Every non-baseline check is fully hidden until the
     * results screen, so this flag is the whole of what a learner can see about
     * the bank while working. At most one per problem; see the partial unique
     * index below.
     */
    isBaseline: boolean('isBaseline').default(false).notNull(),
    /**
     * How hard this particular check is, independent of the problem's own
     * difficulty. Unused by v1 grading (LG-931) — authored now so the bank does
     * not need a backfill when weighting arrives. Reuses `DifficultyLevel`
     * rather than free text so it filters and renames like every other level in
     * the admin.
     */
    difficultyId: uuid('difficultyId').references(() => difficultyLevel.id, {
      onDelete: 'set null',
    }),
    /**
     * Author-chosen display order. Not unique and not a source of meaning on
     * its own — every read tie-breaks on `id`, and the baseline check is found
     * by `isBaseline`, never by being first.
     */
    order: integer('order').default(0).notNull(),
    createdAt: timestamp('createdAt', { withTimezone: true, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { withTimezone: true, mode: 'date' })
      .defaultNow()
      .notNull(),
    deletedAt: timestamp('deletedAt', { withTimezone: true, mode: 'date' }),
  },
  (table) => [
    index('SystemDesignAssertion_problemId_idx').on(table.problemId),
    index('SystemDesignAssertion_difficultyId_idx').on(table.difficultyId),
    // Partial, in the shape `SystemDesignProblemSource` uses for `isOriginal`:
    // a problem holds many hidden checks but at most one visible freebie. The
    // filter also excludes soft-deleted rows, so retiring a baseline frees the
    // slot for its replacement instead of deadlocking the bank.
    uniqueIndex('SystemDesignAssertion_problemId_isBaseline_unique')
      .on(table.problemId)
      .where(sql`${table.isBaseline} AND ${table.deletedAt} IS NULL`),
  ],
)

/**
 * Resource - Reusable external links (videos, articles, courses, books, tools)
 * Can be attached to system design problems and study plans via junction tables
 */
export const resource = pgTable('Resource', {
  id: uuid('id').primaryKey().defaultRandom(),
  title: text('title').notNull(),
  url: text('url').notNull().unique(),
  type: text('type').notNull(), // "video", "article", "course", "book", "tool"
  description: text('description'),
  icon: text('icon'),
  durationMinutes: integer('durationMinutes'),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

/**
 * SystemDesignProblemResource - Links resources to system design problems
 */
export const systemDesignProblemResource = pgTable(
  'SystemDesignProblemResource',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    systemDesignProblemId: uuid('systemDesignProblemId')
      .notNull()
      .references(() => systemDesignProblem.id, { onDelete: 'cascade' }),
    resourceId: uuid('resourceId')
      .notNull()
      .references(() => resource.id, { onDelete: 'cascade' }),
    order: integer('order').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('SystemDesignProblemResource_problem_resource_unique').on(
      table.systemDesignProblemId,
      table.resourceId,
    ),
    index('SystemDesignProblemResource_systemDesignProblemId_idx').on(
      table.systemDesignProblemId,
    ),
    index('SystemDesignProblemResource_resourceId_idx').on(table.resourceId),
  ],
)

// =============================================================================
// RELATIONS
// =============================================================================

export const codingExerciseRelations = relations(
  codingExercise,
  ({ one, many }) => ({
    difficulty: one(difficultyLevel, {
      fields: [codingExercise.difficultyId],
      references: [difficultyLevel.id],
    }),
    sources: many(codingExerciseSource),
    homework: many(coachingHomeworkExercise),
  }),
)

export const codingExerciseSourceRelations = relations(
  codingExerciseSource,
  ({ one }) => ({
    exercise: one(codingExercise, {
      fields: [codingExerciseSource.exerciseId],
      references: [codingExercise.id],
    }),
    source: one(source, {
      fields: [codingExerciseSource.sourceId],
      references: [source.id],
    }),
  }),
)

export const systemDesignProblemRelations = relations(
  systemDesignProblem,
  ({ one, many }) => ({
    difficulty: one(difficultyLevel, {
      fields: [systemDesignProblem.difficultyId],
      references: [difficultyLevel.id],
    }),
    owningSource: one(source, {
      fields: [systemDesignProblem.sourceId],
      references: [source.id],
    }),
    sources: many(systemDesignProblemSource),
    companyTags: many(systemDesignProblemCompanyTag),
    resources: many(systemDesignProblemResource),
    homework: many(coachingHomeworkSystemDesign),
    studyPlans: many(studyPlanSystemDesign),
    interviewLinks: many(systemDesignInterviewExperience),
    assertions: many(systemDesignAssertion),
  }),
)

export const systemDesignAssertionRelations = relations(
  systemDesignAssertion,
  ({ one }) => ({
    problem: one(systemDesignProblem, {
      fields: [systemDesignAssertion.problemId],
      references: [systemDesignProblem.id],
    }),
    difficulty: one(difficultyLevel, {
      fields: [systemDesignAssertion.difficultyId],
      references: [difficultyLevel.id],
    }),
  }),
)

export const systemDesignProblemSourceRelations = relations(
  systemDesignProblemSource,
  ({ one }) => ({
    problem: one(systemDesignProblem, {
      fields: [systemDesignProblemSource.problemId],
      references: [systemDesignProblem.id],
    }),
    source: one(source, {
      fields: [systemDesignProblemSource.sourceId],
      references: [source.id],
    }),
  }),
)

export const systemDesignProblemCompanyTagRelations = relations(
  systemDesignProblemCompanyTag,
  ({ one }) => ({
    problem: one(systemDesignProblem, {
      fields: [systemDesignProblemCompanyTag.problemId],
      references: [systemDesignProblem.id],
    }),
    companyTag: one(companyTag, {
      fields: [systemDesignProblemCompanyTag.companyTagId],
      references: [companyTag.id],
    }),
  }),
)

export const resourceRelations = relations(resource, ({ many }) => ({
  systemDesignProblems: many(systemDesignProblemResource),
  studyPlans: many(studyPlanResource),
}))

export const systemDesignProblemResourceRelations = relations(
  systemDesignProblemResource,
  ({ one }) => ({
    systemDesignProblem: one(systemDesignProblem, {
      fields: [systemDesignProblemResource.systemDesignProblemId],
      references: [systemDesignProblem.id],
    }),
    resource: one(resource, {
      fields: [systemDesignProblemResource.resourceId],
      references: [resource.id],
    }),
  }),
)

export const taskRelations = relations(task, ({ one, many }) => ({
  homework: many(coachingHomeworkTask),
  source: one(source, {
    fields: [task.sourceId],
    references: [source.id],
  }),
  sourceContent: one(sourceContent, {
    fields: [task.sourceContentId],
    references: [sourceContent.id],
  }),
  resource: one(resource, {
    fields: [task.resourceId],
    references: [resource.id],
  }),
}))

export const sourceContentRelations = relations(
  sourceContent,
  ({ one, many }) => ({
    source: one(source, {
      fields: [sourceContent.sourceId],
      references: [source.id],
    }),
    parent: one(sourceContent, {
      fields: [sourceContent.parentId],
      references: [sourceContent.id],
      relationName: 'sourceContentHierarchy',
    }),
    children: many(sourceContent, { relationName: 'sourceContentHierarchy' }),
    homework: many(coachingHomeworkSourceContent),
  }),
)

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type CodingExercise = typeof codingExercise.$inferSelect
export type NewCodingExercise = typeof codingExercise.$inferInsert

export type SystemDesignProblem = typeof systemDesignProblem.$inferSelect
export type NewSystemDesignProblem = typeof systemDesignProblem.$inferInsert

export type Task = typeof task.$inferSelect
export type NewTask = typeof task.$inferInsert

export type SourceContent = typeof sourceContent.$inferSelect
export type NewSourceContent = typeof sourceContent.$inferInsert

export type CodingExerciseSource = typeof codingExerciseSource.$inferSelect
export type NewCodingExerciseSource = typeof codingExerciseSource.$inferInsert

export type SystemDesignProblemSource =
  typeof systemDesignProblemSource.$inferSelect
export type NewSystemDesignProblemSource =
  typeof systemDesignProblemSource.$inferInsert

export type SystemDesignProblemCompanyTag =
  typeof systemDesignProblemCompanyTag.$inferSelect
export type NewSystemDesignProblemCompanyTag =
  typeof systemDesignProblemCompanyTag.$inferInsert

export type Resource = typeof resource.$inferSelect
export type NewResource = typeof resource.$inferInsert

export type SystemDesignProblemResource =
  typeof systemDesignProblemResource.$inferSelect
export type NewSystemDesignProblemResource =
  typeof systemDesignProblemResource.$inferInsert
